import { Edit } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  InputLabel,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { stripeUrl } from "common/const";
import { EditPaymentMethodDialogue } from "components/settings/shared/billing-details/editPaymentDialogue";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { PaymentMethod } from "data/models";
import { ReactQueryMutationError, addPaymentMethod } from "data/mutations";
import {
  fetchOrganisation,
  fetchPaymentMethod,
  fetchSubscription,
} from "data/queries";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";

export const BillingDetails = () => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const history = useHistory();

  const organisationQuery = useQuery([QueryKeys.organisation, identityId], () =>
    fetchOrganisation()
  );
  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription()
  );
  const paymentMethodQuery = useQuery(
    [QueryKeys.paymentMethod, identityId],
    () => fetchPaymentMethod()
  );

  const stripe = useStripe();
  const stripeElements = useElements();

  const [editingCC, setEditingCC] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const paymentMethodMutation = useMutation<
    PaymentMethod,
    ReactQueryMutationError
  >(() => addPaymentMethod(stripe, stripeElements, setEditingCC), {
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  const isLoading: boolean =
    organisationQuery.isLoading ||
    subscriptionQuery.isLoading ||
    paymentMethodQuery.isLoading;

  const isSuccess: boolean =
    organisationQuery.isSuccess &&
    subscriptionQuery.isSuccess &&
    paymentMethodQuery.isSuccess;

  const isError: boolean =
    organisationQuery.isError ||
    subscriptionQuery.isError ||
    paymentMethodQuery.isError;

  const hasActiveSubscription: boolean =
    isSuccess && !!subscriptionQuery.data!.activeSubscription;

  const isExternallyManaged =
    subscriptionQuery.data?.activeSubscription?.plan.externally_managed ??
    false;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <CardHeader
          title="Payment Method"
          subheader="Set a maximum spend limit "
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Payment Method" />
          <CardContent>
            {isError && (
              <Alert variant="outlined" severity="error">
                An error occurred while fetching the payment method.
              </Alert>
            )}
            {isLoading && <Skeleton variant="rectangular" height="200px" />}
            {isSuccess && isExternallyManaged && (
              <Alert variant="outlined" severity="info">
                Your payment is managed outside of self-service, please contact
                support for any changes.
              </Alert>
            )}
            {isSuccess && !isExternallyManaged && (
              <>
                <TextField
                  size="small"
                  error={paymentMethodQuery.data === null}
                  id="paymentMethod"
                  name="paymentMethod"
                  label="Payment Method"
                  type="text"
                  value={
                    paymentMethodQuery.data
                      ? `${toTitleCase(paymentMethodQuery.data.method)} .... ${
                          paymentMethodQuery.data.last4
                        } - ${paymentMethodQuery.data.expireMonth}/${
                          paymentMethodQuery.data.expireYear
                        }`
                      : "No Payment Method"
                  }
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={() => setEditingCC(true)}>
                        <Edit />
                      </IconButton>
                    ),
                  }}
                />
                {paymentMethodQuery.data && paymentMethodQuery.data.expired && (
                  <Alert variant="outlined" severity="error">
                    Your credit card has expired
                  </Alert>
                )}
                <EditPaymentMethodDialogue
                  open={editingCC}
                  handleOnClose={handleOnClose}
                  stripeUrl={stripeUrl}
                  paymentMethodMutation={paymentMethodMutation}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <CardHeader
          title="Billing Details"
          subheader="Contact information used on invoices and receipts"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            {isError && (
              <Alert variant="outlined" severity="error">
                An error occurred while fetching the billing deatils.
              </Alert>
            )}

            {isLoading && <Skeleton variant="rectangular" height="200px" />}
            {isSuccess && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <InputLabel>Organisation Name:</InputLabel>
                  <Typography variant="body2">
                    {organisationQuery.data?.name}
                  </Typography>
                </Box>
                <Box>
                  <InputLabel>Billing Email:</InputLabel>
                  <Typography variant="body2">
                    {organisationQuery.data?.email}
                  </Typography>
                </Box>
                <Box>
                  <InputLabel>Address</InputLabel>
                  <Typography variant="body2">
                    {organisationQuery.data?.address
                      ? organisationQuery.data?.address
                      : "-"}
                  </Typography>
                </Box>
                <Alert
                  severity="warning"
                  variant="outlined"
                  style={{ marginTop: "32px", marginBottom: "28px" }}
                  action={
                    <Button
                      color="inherit"
                      onClick={() => history.push(Routes.orgGeneralSettings)}
                    >
                      Settings
                    </Button>
                  }
                >
                  Billing details are the same as your general settings.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
